import { faCaretDown, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  IconButton,
  Menu,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import { styled } from '@mui/material/styles'
import _ from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
/** TRANSLATION */
import { useTranslation } from 'react-i18next'
/** STYLE / CONSTANTS */
import { colors } from 'constants/theme'
/** API */
import ComponentApi from 'api/components-api'
/** COMPONENT */
import InputTextField from 'component/Input/InputTextField'
import TextInput from 'new-components/inputs/text-input'
import { useDispatch } from 'react-redux'

const InputAddress = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: 0,
  backgroundColor: 'transparent !important',
  position: 'relative',
  textTransform: 'none',
  '.MuiButton-endIcon': {
    position: 'absolute',
    right: 14,
    svg: {
      fontSize: 16,
      color: colors.black
    }
  },
  '.MuiOutlinedInput-input': {
    textOverflow: 'ellipsis',
    marginRight: 10
  },
  '&[aria-expanded="true"]': {
    '.MuiButton-endIcon': {
      transform: 'rotate(180deg)'
    }
  }
}))

const MenuDropdown = styled(Menu)(({ theme }) => ({
  '.MuiPaper-root': {
    overflow: 'hidden',
    boxShadow: `0 8px 10px 0 ${colors.black12}`,
    borderRadius: 4
  },
  '.MuiList-root': {
    maxHeight: 312,
    marginTop: 4,
    padding: 0,
    paddingBottom: 16
  }
}))

const HeaderTabsRow = styled(Tabs)(({ theme }) => ({
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    height: 2,
    width: '100%',
    backgroundColor: colors.extraLightBlue
  },
  '.MuiTabs-indicator': {
    backgroundColor: colors.themeSecondColor
  }
}))

const HeaderTab = styled(Tab)(({ theme }) => ({
  width: '33.33%',
  paddingLeft: 0,
  paddingRight: 0,
  fontSize: 16,
  color: colors.textPrimary,
  '&.Mui-selected': {
    color: colors.themeSecondColor
  }
}))

const BodyTabPanel = styled('div')(({ theme }) => ({
  maxHeight: 'calc(312px - 48px - 16px)',
  overflowX: 'hidden',
  overflowY: 'auto',
  marginTop: 6
}))

const ListItem = styled('div')(({ theme }) => ({
  fontSize: 14,
  color: colors.textPrimary,
  padding: '0.375rem 1.5rem',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colors.themeSecondColor06,
    color: colors.themeSecondColor
  }
}))

type AddressProps = {
  patientId: number | string
  countryId?: number | string
  provinceId?: number | string
  districtId?: number
  subDistrictId?: number
  postcode?: string
  disabled?: boolean
  label?: string
  provinceText?: string
  districtText?: string
  subDistrictText?: string
  callbackAddress?: (val: any) => void
  required?: boolean
  height?: string
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const addressValue = {
  provinceId: 0,
  districtId: 0,
  subDistrictId: 0,
  postcode: ''
}

export default function SelectAddress(props: AddressProps) {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const lang = i18n?.language || 'th'
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [dropdownWidth, setDropdownWidth] = useState(0)
  const [tab, setTab] = useState(0)
  const [provinces, setProvinces] = useState<any[]>([])
  const [districts, setDistricts] = useState<any[]>([])
  const [subDistricts, setSubDistricts] = useState<any[]>([])
  const [address, setAddress] = useState(addressValue)
  const [disabledField, setDisabledField] = useState(false)
  const [searchProvince, setSearchProvince] = useState('')
  const [searchDistrict, setSearchDistrict] = useState('')
  const [searchSubDistrict, setSearchSubDistrict] = useState('')

  const provinceInputRef = useRef<HTMLInputElement>(null)
  const districtInputRef = useRef<HTMLInputElement>(null)
  const subDistrictInputRef = useRef<HTMLInputElement>(null)
  const isInitialMount = useRef(true)

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    const widthElements = document.getElementsByClassName('select-address')
    setDropdownWidth(Number(widthElements[0]?.getBoundingClientRect().width))
    setAnchorEl(event.currentTarget)
    setTab(0)
  }
  const handleCloseMenu = () => {
    if (props.required) {
      if (!address?.provinceId || !address?.districtId || !address?.subDistrictId) {
        return
      }
    }
    setAnchorEl(null)
  }

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
    setTimeout(() => {
      if (newValue === 0 && provinceInputRef.current) provinceInputRef.current.focus()
      if (newValue === 1 && districtInputRef.current) districtInputRef.current.focus()
      if (newValue === 2 && subDistrictInputRef.current) subDistrictInputRef.current.focus()
    }, 100)
  }

  const loadProvinces = async () => {
    if (sessionStorage.getItem('provinces')) {
      const items: any = sessionStorage.getItem('provinces')
      setProvinces(JSON.parse(items))
    } else {
      const resProvinces = await ComponentApi.findAllProvinces()
      if (resProvinces.status === 200) {
        const items: any = _.orderBy(
          resProvinces.data,
          ['provinceNameTh', 'provinceNameEn'],
          ['asc', 'desc']
        )
        setProvinces(items)
        sessionStorage.setItem('provinces', JSON.stringify(items))
      }
    }
  }

  const loadDistricts = async (provinceId: string) => {
    const resDistricts = await ComponentApi.findAllDistricts(provinceId)
    if (resDistricts.status === 200) {
      const items: any = _.orderBy(
        resDistricts?.data,
        ['districtNameTh', 'districtNameEn'],
        ['asc', 'desc']
      )
      setDistricts(items)
      if (props?.districtText) {
        const district: any = _.find(
          items,
          (val: any) => val.districtNameTh.search(props?.districtText) !== -1
        )
        if (district) {
          setAddress((prev) => ({
            ...prev,
            districtId: district?.districtId,
            subDistrictId: 0,
            postcode: ''
          }))
          loadSubDistricts(district?.districtId.toString(), provinceId)
          setTab(2)
        }
      } else if (props?.districtId) {
        loadSubDistricts(String(props?.districtId))
      }
    }
  }

  const loadSubDistricts = async (districtId: string, provinceId = '') => {
    const resSubDistricts = await ComponentApi.findAllSubDistricts(districtId)
    if (resSubDistricts.status === 200) {
      const items: any = _.orderBy(
        resSubDistricts.data,
        ['subDistrictNameTh', 'subDistrictNameEn'],
        ['asc', 'desc']
      )
      setSubDistricts(items)
      if (props?.subDistrictText) {
        const subDistrict: any = _.find(
          items,
          (val: any) =>
            val.subDistrictNameTh.search(props?.subDistrictText) !== -1
        )
        if (subDistrict) {
          setAddress((prev) => ({
            ...prev,
            provinceId: provinceId ? parseInt(provinceId) : prev?.provinceId,
            districtId: parseInt(districtId),
            subDistrictId: subDistrict?.subDistrictId,
            postcode: subDistrict?.postcode
          }))
          handleCloseMenu()
        }
      }
    }
  }

  const renderProvince = () => {
    const province: any = _.find(
      provinces,
      (val: any) =>
        props?.provinceText && val?.provinceNameTh.search(props?.provinceText) !== -1
    )
    if (
      props?.provinceText &&
      props?.districtText &&
      props?.subDistrictText &&
      province
    ) {
      // If callers (e.g. FormRetrieve's id-card resolver) have already
      // populated patient.provinceId/districtId/subDistrictId/postcode, the
      // matching address state is already loaded — don't blow it away with a
      // text-based reload that re-fires district/subdistrict API calls and
      // resets dependent fields.
      if (
        address.provinceId === province?.provinceId &&
        address.districtId &&
        address.subDistrictId
      ) {
        return
      }
      setAddress((prev) => ({
        ...prev,
        provinceId: province?.provinceId,
        districtId: 0,
        subDistrictId: 0,
        postcode: ''
      }))
      setDistricts([])
      loadDistricts(province?.provinceId.toString())
      setTab(1)
    }
  }

  const getSelectedProvince = () =>
    address.provinceId ? _.find(provinces, { provinceId: address?.provinceId }) : null
  const getSelectedDistrict = () =>
    address.districtId ? _.find(districts, { districtId: address?.districtId }) : null
  const getSelectedSubDistrict = () =>
    address.subDistrictId ? _.find(subDistricts, { subDistrictId: address?.subDistrictId }) : null

  const handleProvinceSelect = (selected: any) => {
    setAddress((prev) => ({
      ...prev,
      provinceId: selected?.provinceId,
      districtId: 0,
      subDistrictId: 0,
      postcode: ''
    }))
    setSearchProvince(selected?.provinceNameTh)
    setSearchDistrict('')
    setSearchSubDistrict('')
    setDistricts([])
    loadDistricts(selected?.provinceId.toString())
    setTab(1)
    setTimeout(() => {
      if (districtInputRef.current) districtInputRef.current.focus()
    }, 100)
  }

  const handleDistrictSelect = (selected: any) => {
    setAddress((prev) => ({
      ...prev,
      districtId: selected?.districtId,
      subDistrictId: 0,
      postcode: ''
    }))
    setSearchDistrict(selected?.districtNameTh)
    setSearchSubDistrict('')
    loadSubDistricts(selected?.districtId.toString())
    setTab(2)
    setTimeout(() => {
      if (subDistrictInputRef.current) subDistrictInputRef.current.focus()
    }, 100)
  }

  const handleSubDistrictSelect = (selected: any) => {
    setAddress((prev) => ({
      ...prev,
      subDistrictId: selected?.subDistrictId,
      postcode: selected?.postcode
    }))
    setSearchSubDistrict(selected?.subDistrictNameTh)
    if (!props.required) {
      handleCloseMenu()
    }
  }

  const clearProvince = () => {
    setAddress((prev) => ({
      ...prev,
      provinceId: 0,
      districtId: 0,
      subDistrictId: 0,
      postcode: ''
    }))
    setSearchProvince('')
    setTab(0)
  }
  const clearDistrict = () => {
    setAddress((prev) => ({
      ...prev,
      districtId: 0,
      subDistrictId: 0,
      postcode: ''
    }))
    setSearchDistrict('')
    setTab(1)
  }
  const clearSubDistrict = () => {
    setAddress((prev) => ({ ...prev, subDistrictId: 0, postcode: '' }))
    setSearchSubDistrict('')
    setTab(2)

  }
  const filteredProvinces = useMemo(() => {
    const term = !getSelectedProvince() && searchProvince ? searchProvince.toLowerCase() : ''
    return term ? provinces.filter((p: any) =>
      p?.provinceNameTh.toLowerCase().includes(term)
    ) : provinces

  }, [searchProvince, provinces, address?.provinceId])

  const filteredDistricts = useMemo(() => {
    const term = !getSelectedDistrict() && searchDistrict ? searchDistrict.toLowerCase() : ''
    return term ? districts.filter((d: any) =>
      d?.districtNameTh.toLowerCase().includes(term)
    ) : districts
  }, [searchDistrict, districts, address?.districtId])

  const filteredSubDistricts = useMemo(() => {
    const term = !getSelectedSubDistrict() && searchSubDistrict ? searchSubDistrict.toLowerCase() : ''
    return term ? subDistricts.filter((s: any) =>
      s?.subDistrictNameTh.toLowerCase().includes(term)
    ) : subDistricts
  }, [searchSubDistrict, subDistricts, address?.subDistrictId])

  useEffect(() => {
    renderProvince()
  }, [props?.provinceText, props?.districtText, props?.subDistrictText, provinces])


  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (
      props.callbackAddress &&
      address.provinceId >= 0 &&
      address.districtId >= 0 &&
      address.subDistrictId >= 0
    ) {
      props.callbackAddress(address)
    }
  }, [
    address.provinceId,
    address.districtId,
    address.subDistrictId
  ])

  // useEffect(() => {
  //   if (props?.callbackAddress && (address?.subDistrictId >= 0 || address?.provinceId >= 0 || address?.districtId >= 0 || !address?.postcode)) {
  //     props?.callbackAddress(address)
  //   }
  // }, [address?.subDistrictId, address?.provinceId, address?.districtId, address?.postcode])


  useEffect(() => {
    loadProvinces()
    const hasText = !!(props?.provinceText || props?.districtText || props?.subDistrictText)
    if (props?.provinceId && !hasText) {
      setAddress({
        provinceId:
          typeof props?.provinceId === 'string'
            ? parseInt(props?.provinceId)
            : props?.provinceId || 0,
        districtId: props?.districtId || 0,
        subDistrictId: props?.subDistrictId || 0,
        postcode: props?.postcode || ''
      })
      loadDistricts(
        typeof props?.provinceId === 'string'
          ? props?.provinceId
          : String(props?.provinceId)
      )
    } else if (!props?.provinceId && !hasText) {
      setAddress(addressValue)
    }
    // If text props are set, leave address alone — renderProvince (driven by
    // the [provinceText, districtText, subDistrictText, provinces] effect)
    // owns the resolution path, and resetting here races with it.
  }, [props?.patientId, props?.provinceId])

  useEffect(() => {
    if (
      props?.countryId === 219 ||
      props?.countryId === 'Thailand' ||
      props?.countryId === 'TH'
    ) {
      setDisabledField(false)
    } else {
      setAddress(addressValue)
      setDisabledField(true)
    }
  }, [props?.countryId])

  return (
    <div>
      <InputAddress
        id="basic-button"
        className="select-address"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpenMenu}
        endIcon={!open ? <FontAwesomeIcon icon={faCaretDown} /> : <></>}
        disabled={props.disabled || disabledField}
        disableFocusRipple
        disableRipple
      >
        <div className="pe-none w-100">
          <InputTextField
            inputHeight={props.height || '43px'}
            disabled={props.disabled || disabledField}
            label={props.label || t('SELECT_ADDRESS')}
            value={
              address?.provinceId
                ? (lang === "en" ? getSelectedProvince()?.provinceNameEn : getSelectedProvince()?.provinceNameTh) +
                (getSelectedDistrict() ? ', ' + (lang === "en" ? getSelectedDistrict()?.districtNameEn : getSelectedDistrict()?.districtNameTh) : '') +
                (getSelectedSubDistrict() ? ', ' + (lang === "en" ? getSelectedSubDistrict()?.subDistrictNameEn : getSelectedSubDistrict()?.subDistrictNameTh) : '') +
                (address?.postcode ? ', ' + address?.postcode : '')
                : ''
            }
            required={props?.required}
          />
        </div>
      </InputAddress>
      <MenuDropdown
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
        sx={{ '.MuiMenu-list': { width: dropdownWidth } }}
      >
        <Box sx={{ width: '100%' }}>
          <HeaderTabsRow value={tab} onChange={handleChangeTab} aria-label="address tabs">
            <HeaderTab label={t('PROVINCE')} {...tabListProps(0)} />
            <HeaderTab
              className={`${!address?.provinceId ? 'pe-none' : ''}`}
              label={t('DISTRICT')}
              {...tabListProps(1)}
            />
            <HeaderTab
              className={`${!address?.districtId ? 'pe-none' : ''}`}
              label={t('SUBDISTRICT')}
              {...tabListProps(2)}
            />
          </HeaderTabsRow>
          <TabPanel value={tab} index={0}>
            <Box sx={{ paddingLeft: 3, paddingRight: 3, paddingBottom: 1 }}>
              <TextInput
                fullWidth
                placeholder={`${t('INPUT.SEARCH')}${t('PROVINCE')}`}
                value={getSelectedProvince() ? (lang === "en" ? getSelectedProvince().provinceNameEn : getSelectedProvince().provinceNameTh) : searchProvince}
                onChange={(e) => {
                  if (getSelectedProvince()) clearProvince()
                  setSearchProvince(e.target.value)
                }}
                inputProps={{
                  endAdornment: getSelectedProvince() ? (
                    <IconButton onClick={clearProvince} size="small">
                      <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                  ) : null
                }}
              />
            </Box>
            {filteredProvinces?.length <= 0 && searchProvince !== '' ? <ListItem sx={{ textAlign: 'center', color: colors.gray }}><Typography>{t('NOT_FOUND')}</Typography></ListItem>
              : filteredProvinces.map((province: any, index: number) => (
                <ListItem
                  key={index}
                  onClick={() => {
                    handleProvinceSelect(province)
                  }}
                >
                  {lang === "en" ? province.provinceNameEn : province.provinceNameTh}
                </ListItem>
              ))
            }
          </TabPanel>
          <TabPanel value={tab} index={1} >
            <Box sx={{ paddingLeft: 3, paddingRight: 3, paddingBottom: 1 }}>
              <TextInput
                fullWidth
                placeholder={`${t('INPUT.SEARCH')}${t('DISTRICT')}`}
                value={getSelectedDistrict() ? (lang === "en" ? getSelectedDistrict()?.districtNameEn : getSelectedDistrict()?.districtNameTh) : searchDistrict}
                onChange={(e) => {
                  if (getSelectedDistrict()) clearDistrict()
                  setSearchDistrict(e.target.value)
                }}
                inputProps={{
                  endAdornment: getSelectedDistrict() ? (
                    <IconButton onClick={clearDistrict} size="small">
                      <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                  ) : null
                }}
              />
            </Box>
            {filteredDistricts?.length <= 0 && searchDistrict !== '' ? <ListItem sx={{ textAlign: 'center', color: colors.gray }}><Typography>{t('NOT_FOUND')}</Typography></ListItem>
              : filteredDistricts.map((district: any, index: number) => (
                <ListItem
                  key={index}
                  onClick={() => {
                    handleDistrictSelect(district)
                  }}
                >
                  {lang === "en" ? district.districtNameEn : district.districtNameTh}
                </ListItem>
              ))}
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <Box sx={{ paddingLeft: 3, paddingRight: 3, paddingBottom: 1 }}>
              <TextInput
                fullWidth
                placeholder={`${t('INPUT.SEARCH')}${t('SUBDISTRICT')}`}
                value={getSelectedSubDistrict() ? (lang === "en" ? getSelectedSubDistrict()?.subDistrictNameEn : getSelectedSubDistrict()?.subDistrictNameTh) : searchSubDistrict}
                onChange={(e) => {
                  if (getSelectedSubDistrict()) clearSubDistrict()
                  setSearchSubDistrict(e.target.value)
                }}
                inputProps={{
                  endAdornment: getSelectedSubDistrict() ? (
                    <IconButton onClick={clearSubDistrict} size="small">
                      <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                  ) : null
                }}
              />
            </Box>
            {filteredSubDistricts.length <= 0 && searchSubDistrict !== '' ? <ListItem sx={{ textAlign: 'center', color: colors.gray }}><Typography>{t('NOT_FOUND')}</Typography></ListItem>
              : filteredSubDistricts.map((subDistrict: any, index: number) => (
                <ListItem
                  key={index}
                  onClick={() => {
                    handleSubDistrictSelect(subDistrict)
                  }}
                >
                  {lang === "en" ? subDistrict.subDistrictNameEn : subDistrict.subDistrictNameTh}
                </ListItem>
              ))}
          </TabPanel>
        </Box>
      </MenuDropdown>
    </div>
  )
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <BodyTabPanel
      role="tabpanel"
      hidden={value !== index}
      id={`address-tabpanel-${index}`}
      aria-labelledby={`address-tab-${index}`}
      {...other}
      className="custom-scroll"
    > {value === index && <Box>{children}</Box>}
    </BodyTabPanel>
  )
}

function tabListProps(index: number) {
  return {
    id: `address-tab-${index}`,
    'aria-controls': `address-tabpanel-${index}`
  }
}